import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {defaultTo, isNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  useGetLabourCatalogCategoryQuery,
  usePatchLabourCatalogCategoryMutation,
  usePostLabourCatalogCategoryMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {getOptionsFromAuthorizationProfiles, handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {LabourCatalogueForm, LabourCatalogueFormSchema} from './types/LabourCatalogueFormSchema';

export function LabourCataloguesDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data: tenant} = useGetTenantQuery();

  const {data, isLoading, isError} = useGetLabourCatalogCategoryQuery(
    {labourCatalogId: id ?? ''},
    {skip: isNil(id)}
  );

  const [postLabourCatalogCategory] = usePostLabourCatalogCategoryMutation();
  const [patchLabourCatalogCategory] = usePatchLabourCatalogCategoryMutation();

  const {data: authorizationProfileList, isLoading: isAuthorizationProfilesLoading} =
    useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const handleSubmit: FormSubmitHandler<LabourCatalogueForm> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      name: data.name ?? '',
      code: data.code,
      authorizationProfiles: data.authorizationProfiles ?? [],
    };

    if (isNil(id)) {
      return await postLabourCatalogCategory({body})
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.labourCatalogues);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    return await patchLabourCatalogCategory({labourCatalogId: id, body})
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.labourCatalogues);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const titleKey = isNil(id)
    ? 'entity.workshop.actions.newLabourCatalogue'
    : 'entity.workshop.labels.labourCatalogues';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.workshop.actions.newLabourCatalogue'),
          breadcrumbs: [
            {
              label: i18n.t('entity.workshop.labels.labourCatalogues'),
              href: settingsRoutes.labourCatalogues,
            },
          ],
        }}
        isCreating={isNil(id)}
        isLoading={isLoading}
        isError={isError}
        data-testid={testIds.settings.labourCataloguesDetail('template')}
      >
        <SettingsSection>
          <Form<LabourCatalogueForm>
            experimentalZodSchema={LabourCatalogueFormSchema}
            onSubmit={handleSubmit}
            defaultValues={{
              name: defaultTo('', data?.name),
              code: data?.code,
              isEditable: data?.isEditable,
              authorizationProfiles: data?.authorizationProfiles,
            }}
            shouldWatchForUnsavedChanges
          >
            {(control, formApi, navigateWithoutBlocker) => (
              <>
                <VStack spacing={4}>
                  <FormField
                    control={control}
                    label={i18n.t('general.labels.name')}
                    type="text"
                    name="name"
                    isRequired
                    data-testid={testIds.settings.labourCataloguesDetail('name')}
                  />
                  <FormField
                    control={control}
                    label={i18n.t('entity.settings.labels.authorizationProfile')}
                    type="multiChoice"
                    name="authorizationProfiles"
                    options={getOptionsFromAuthorizationProfiles(
                      authorizationProfileList,
                      formApi.watch('authorizationProfiles')
                    )}
                    isLoading={isAuthorizationProfilesLoading}
                    data-testid={testIds.settings.labourCataloguesDetail('authProfile')}
                    isNotClearable
                  />
                </VStack>
                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      title: i18n.t('general.actions.discardChanges'),
                      variant: 'secondary',
                      onClick: () => navigateWithoutBlocker(settingsRoutes.labourCatalogues),
                    },
                    {
                      control,
                      type: 'form-button',
                      buttonType: 'submit',
                      title: i18n.t('general.actions.saveChanges'),
                      'data-testid': testIds.settings.labourCataloguesDetail('save'),
                    },
                  ]}
                />
              </>
            )}
          </Form>
        </SettingsSection>
      </SettingsTemplate>
    </>
  );
}
